<script setup lang="ts">
import { useForm } from 'vee-validate'
import { ref } from 'vue'
import { z } from 'zod'

import ActionButton from '~/components/shared/ActionButton.vue'
import TrackUniversalPlayer from '~/components/track/universalPlayer.vue'
import HeightExpand from '~/components/transitions/heightExpand.vue'
import VCheckbox from '~/components/ui/Inputs/VCheckbox.vue'
import VTextInput from '~/components/ui/Inputs/VText.vue'
import VDatePicker from '~/components/ui/VDatePicker.vue'

import { useFieldValidation } from '~/composables/useFieldValidation'

import {
  draftTrackInitialState as trackInitialStateFactory,
  useDraftTrackStore,
} from '~/stores/draftTrack'
import { draftTrackTagsInitialState as trackTagsInitialStateFactory } from '~/stores/draftTrackTags'

import {
  isBandcampUrl,
  isSoundCloudUrl,
  isSpotifyUrl,
  isYoutubeUrl,
} from '~/utils/url-validation'

import type Track from '~/types/track'

type Props = {
  bandId: number
}

type Emits = {
  new: [payload: Track | undefined]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n()
const { UPDATE_ON_SERVER: UPDATE_TRACK } = useDraftTrackStore()

const { validate: validateForm } = useForm<{
  link: string
  secondary_link: string
  name: string
}>()

const { value: linkFieldValue, errorMessage: linkFieldErrorMessage } =
  useFieldValidation<string>(
    'link',
    z
      .string()
      .min(1, t('error.classic'))
      .refine((value) => validateLink(value), {
        message: t('inputs.invalid_link'),
      }),
    {
      initialValue: '',
      validateOnValueUpdate: false,
    },
  )

const {
  value: secondaryLinkFieldValue,
  errorMessage: secondaryLinkFieldErrorMessage,
} = useFieldValidation<string>(
  'secondary_link',
  z.string().refine(
    (value) => {
      return value.length === 0 || isSpotifyUrl(value) || isBandcampUrl(value)
    },
    { message: t('inputs.invalid_link') },
  ),
  {
    initialValue: '',
    validateOnValueUpdate: false,
  },
)

const { value: nameFieldValue, errorMessage: nameFieldErrorMessage } =
  useFieldValidation<string>('name', z.string().min(1, t('error.classic')), {
    initialValue: '',
    validateOnValueUpdate: false,
  })

// other fields that don't require validation
const releaseDate = ref<string | null>(null)
const isPublic = ref(false)

// other data values
const loading = ref(false)
const displayPreview = ref(false)
const to = ref<number | null>(null)

// set new preview display when link changes
watch(linkFieldValue, (newVal) => {
  if (import.meta.server) return

  if (to.value !== null) {
    window.clearTimeout(to.value)
    to.value = null
  }
  to.value = window.setTimeout(() => {
    displayPreview.value = validateLink(newVal)
    to.value = null
  }, 300)
})

// make sure isPublic is set to false if the release date is in the future
watch(releaseDate, (newVal) => {
  if (newVal) {
    const now = new Date().getTime()
    const date = new Date(newVal).getTime()

    if (date > now) isPublic.value = false
  }
})

// methods
function validateLink(value: string) {
  return (isYoutubeUrl(value) || isSoundCloudUrl(value)) !== false
}

async function finalize() {
  if (loading.value) return

  const { valid } = await validateForm()
  if (!valid) return

  const patchData = {
    ...trackInitialStateFactory(),
    band: props.bandId,
    link: linkFieldValue.value,
    secondary_link: secondaryLinkFieldValue.value,
    name: nameFieldValue.value,
    release_date: releaseDate.value,
    is_public: isPublic.value,
    tags: trackTagsInitialStateFactory(),
  }

  loading.value = true

  UPDATE_TRACK({
    ...patchData,
  })
    .then((resp) => {
      emit('new', resp)
    })
    .catch((err) => {
      console.error(err)
    })
}
</script>

<template>
  <div class="trackQuickCreateWrapper">
    <div class="title">
      {{ $t('track.quickCreate00') }}
    </div>
    <div class="subtitle">
      {{ $t('track.quickCreate01') }}
    </div>
    <div class="tiWrap long">
      <VTextInput
        v-model="linkFieldValue"
        :label="$t('track.quickCreate02')"
        :placeholder="$t('track.quickCreate03')"
        :p-validity="!linkFieldErrorMessage"
        @enter="finalize"
      >
        <template #err-message>{{ linkFieldErrorMessage }}</template>
      </VTextInput>
    </div>
    <HeightExpand>
      <div v-if="displayPreview" class="preview">
        <TrackUniversalPlayer :link="linkFieldValue" />
      </div>
    </HeightExpand>
    <div class="tiWrap long">
      <VTextInput
        v-model="secondaryLinkFieldValue"
        :label="$t('track.quickCreate04')"
        :label-annotation="$t('common.optional')"
        :placeholder="$t('track.quickCreate05')"
        :p-validity="!secondaryLinkFieldErrorMessage"
        @enter="finalize"
      >
        <template #err-message>{{ secondaryLinkFieldErrorMessage }}</template>
      </VTextInput>
    </div>
    <div class="tiWrap long">
      <VTextInput
        v-model="nameFieldValue"
        :label="$t('track.quickCreate06')"
        :placeholder="$t('track.quickCreate07')"
        :p-validity="!nameFieldErrorMessage"
        @enter="finalize"
      >
        <template #err-message>{{ nameFieldErrorMessage }}</template>
      </VTextInput>
    </div>
    <div class="tiWrap long">
      <VDatePicker
        v-model="releaseDate"
        :label="$t('track.quickCreate08')"
        :placeholder="$t('track.quickCreate09')"
      ></VDatePicker>
    </div>
    <div class="tiWrap">
      <VCheckbox v-model="isPublic" :label="$t('track.quickCreate10')" />
    </div>
    <div class="actionWrap">
      <ActionButton @click="finalize">
        <div class="btnText">
          {{ $t('track.quickCreate11') }}
        </div>
      </ActionButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.title {
  font-size: var(--font-size-title);
  color: var(--color-black);
  font-weight: 700;
  margin-bottom: 2px;
}

.subtitle {
  font-size: var(--font-size);
  font-weight: 300;
  color: var(--color-masala-grey-h);
  margin-bottom: 32px;
}

.tiWrap {
  margin-bottom: 32px;
}

.tiWrap:not(.long) {
  max-width: 224px;
}

.actionWrap {
  display: flex;
  justify-content: center;
}

.preview {
  max-width: 270px;
  margin-bottom: 32px;
}

@media screen and (max-width: 720px) {
  .tiWrap:not(.long),
  .preview {
    max-width: unset;
  }
}
</style>
